'use strict';

/**
 * ns-utils.js
 * North Shore Balloon Decor — vanilla JS utility layer
 *
 * Replaces React context hooks (CartContext, FavoritesContext,
 * RecentlyViewedContext, Accordion, Motion) from the Next.js site.
 * No dependencies. Works as a plain <script> tag.
 *
 * Exposed globals:
 *   window.nsFavorites
 *   window.nsRecentlyViewed
 *   window.nsCart
 *   window.nsAccordion
 *   window.nsAnimate
 */

(function () {

  // ---------------------------------------------------------------------------
  // Storage helpers
  // ---------------------------------------------------------------------------

  function storageGet(key, fallback) {
    try {
      var raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      return JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  }

  function storageSet(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      // Ignore storage errors (private browsing, quota exceeded, etc.)
    }
  }

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------

  function updateCountBadges(selector, count) {
    var elements = document.querySelectorAll(selector);
    for (var i = 0; i < elements.length; i++) {
      elements[i].textContent = count;
    }
  }

  function fireEvent(name, detail) {
    var event;
    if (typeof CustomEvent === 'function') {
      event = new CustomEvent(name, { bubbles: true, detail: detail || {} });
    } else {
      // IE11 fallback (unlikely in Shopify context, but safe)
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(name, true, false, detail || {});
    }
    document.dispatchEvent(event);
  }

  // ---------------------------------------------------------------------------
  // 1. nsFavorites
  // Source: FavoritesContext.tsx — localStorage key "ns-favorites"
  // ---------------------------------------------------------------------------

  var FAVORITES_KEY = 'ns-fav-handles';

  var nsFavorites = {

    /**
     * Returns the full array of favorited product handles.
     * @returns {string[]}
     */
    get: function () {
      var stored = storageGet(FAVORITES_KEY, []);
      return Array.isArray(stored) ? stored : [];
    },

    /**
     * Adds the handle if not present, removes it if it is.
     * Saves back to localStorage and fires ns:favorites:change on document.
     * @param {string} handle
     */
    toggle: function (handle) {
      var current = nsFavorites.get();
      var next = current.indexOf(handle) === -1
        ? current.concat([handle])
        : current.filter(function (h) { return h !== handle; });
      storageSet(FAVORITES_KEY, next);
      nsFavorites.count(); // refresh badges
      fireEvent('ns:fav:change', { handle: handle, favorites: next });
    },

    /**
     * Returns true if the handle is in favorites.
     * @param {string} handle
     * @returns {boolean}
     */
    has: function (handle) {
      return nsFavorites.get().indexOf(handle) !== -1;
    },

    /**
     * Returns the number of favorited items.
     * Also updates all [data-ns-favorites-count] elements in the DOM.
     * @returns {number}
     */
    count: function () {
      var n = nsFavorites.get().length;
      updateCountBadges('[data-ns-favorites-count]', n);
      return n;
    }
  };

  // ---------------------------------------------------------------------------
  // 2. nsRecentlyViewed
  // Source: RecentlyViewedContext.tsx — localStorage key "ns-recently-viewed"
  // Note: spec says cap at 10; original Next.js capped at 6. Using 10 per spec.
  // ---------------------------------------------------------------------------

  var RECENTLY_VIEWED_KEY = 'ns-recently-viewed';
  var RECENTLY_VIEWED_MAX = 10;

  var nsRecentlyViewed = {

    /**
     * Records a product at the front of the recently-viewed list.
     * Dedupes by handle. Caps the list at RECENTLY_VIEWED_MAX items.
     * @param {{ handle: string, title: string, price: number, image: string }} product
     */
    record: function (product) {
      if (!product || !product.handle) return;
      var current = nsRecentlyViewed.get();
      var filtered = current.filter(function (p) { return p.handle !== product.handle; });
      var next = [product].concat(filtered).slice(0, RECENTLY_VIEWED_MAX);
      storageSet(RECENTLY_VIEWED_KEY, next);
    },

    /**
     * Returns the array of recently-viewed products (most recent first).
     * @returns {Array<{handle: string, title: string, price: number, image: string}>}
     */
    get: function () {
      var stored = storageGet(RECENTLY_VIEWED_KEY, []);
      return Array.isArray(stored) ? stored : [];
    }
  };

  // ---------------------------------------------------------------------------
  // 3. nsCart
  // Source: CartContext.tsx — uses Shopify Ajax API (not localStorage)
  // ---------------------------------------------------------------------------

  var nsCart = {

    /**
     * Adds a variant to the Shopify cart via the Ajax API.
     * Fires ns:cart:add on success.
     * @param {number|string} variantId
     * @param {number} quantity
     * @param {Object} [properties]  — line item properties (optional)
     * @returns {Promise}
     */
    add: function (variantId, quantity, properties) {
      var payload = {
        id: variantId,
        quantity: quantity || 1
      };
      if (properties && typeof properties === 'object') {
        payload.properties = properties;
      }

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error(err.description || 'Cart add failed');
          });
        }
        return response.json();
      })
      .then(function (data) {
        // Refresh cart count badge after a successful add
        nsCart.getCount();
        fireEvent('ns:cart:add', { item: data });
        return data;
      });
    },

    /**
     * Dispatches ns:cart:open on document.
     * Empire's cart drawer (or a custom listener) should respond to this event.
     */
    open: function () {
      fireEvent('ns:cart:open', {});
    },

    /**
     * Fetches the current cart from Shopify and returns item_count.
     * Also updates all [data-ns-cart-count] elements in the DOM.
     * @returns {Promise<number>}
     */
    getCount: function () {
      return fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var count = cart.item_count || 0;
        updateCountBadges('[data-ns-cart-count]', count);
        return count;
      });
    }
  };

  // ---------------------------------------------------------------------------
  // 4. nsAccordion
  // Source: Accordion.tsx — one-open-at-a-time within a container
  //
  // Expected markup:
  //   <div data-ns-accordion>
  //     <div class="ns-accordion-item">
  //       <button class="ns-accordion-trigger" aria-expanded="false">Question</button>
  //       <div class="ns-accordion-body" hidden>Answer</div>
  //     </div>
  //     ...
  //   </div>
  // ---------------------------------------------------------------------------

  var nsAccordion = {

    /**
     * Initializes accordion behaviour within a single container element.
     * Clicking a trigger closes all other items in the same container.
     * @param {Element} container
     */
    init: function (container) {
      if (!container) return;
      var items = container.querySelectorAll('.ns-accordion-item');

      for (var i = 0; i < items.length; i++) {
        (function (item) {
          var trigger = item.querySelector('.ns-accordion-trigger');
          var body    = item.querySelector('.ns-accordion-body');

          if (!trigger || !body) return;

          // Set initial ARIA state based on hidden attribute
          var isOpen = !body.hasAttribute('hidden');
          trigger.setAttribute('aria-expanded', String(isOpen));

          trigger.addEventListener('click', function () {
            var currentlyOpen = trigger.getAttribute('aria-expanded') === 'true';

            // Close all siblings first
            var allItems = container.querySelectorAll('.ns-accordion-item');
            for (var j = 0; j < allItems.length; j++) {
              var siblingTrigger = allItems[j].querySelector('.ns-accordion-trigger');
              var siblingBody    = allItems[j].querySelector('.ns-accordion-body');
              if (siblingTrigger && siblingBody) {
                siblingTrigger.setAttribute('aria-expanded', 'false');
                siblingBody.setAttribute('hidden', '');
              }
            }

            // Toggle the clicked item open (unless it was already open)
            if (!currentlyOpen) {
              trigger.setAttribute('aria-expanded', 'true');
              body.removeAttribute('hidden');
            }
          });
        })(items[i]);
      }
    },

    /**
     * Calls init() on every [data-ns-accordion] element in the document.
     */
    initAll: function () {
      var containers = document.querySelectorAll('[data-ns-accordion]');
      for (var i = 0; i < containers.length; i++) {
        nsAccordion.init(containers[i]);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // 5. nsAnimate
  // Source: Motion.tsx (FadeUp / FadeIn via IntersectionObserver)
  //
  // Elements marked data-ns-animate enter the viewport → class ns-is-visible added.
  // CSS in theme.css.liquid defines @keyframes nsFadeUp and the transition.
  //
  // Optional: data-ns-animate-delay="0.2" sets a CSS custom property --ns-delay
  // so staggered animations work without JS loops.
  // ---------------------------------------------------------------------------

  var nsAnimate = {

    _observer: null,

    /**
     * Creates the IntersectionObserver and observes all [data-ns-animate] elements.
     * Safe to call multiple times — tears down the previous observer first.
     */
    setup: function () {
      if (!window.IntersectionObserver) return; // graceful degradation

      // Disconnect any previously created observer
      if (nsAnimate._observer) {
        nsAnimate._observer.disconnect();
      }

      nsAnimate._observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.isIntersecting) {
            var el = entry.target;
            var delay = el.getAttribute('data-ns-animate-delay');
            if (delay) {
              el.style.setProperty('--ns-delay', delay + 's');
            }
            el.classList.add('ns-is-visible');
            nsAnimate._observer.unobserve(el); // fire once, like framer-motion viewport.once
          }
        }
      }, {
        threshold: 0.15,
        rootMargin: '-80px 0px' // mirrors framer-motion VIEWPORT margin: '-80px'
      });

      var targets = document.querySelectorAll('[data-ns-animate]');
      for (var i = 0; i < targets.length; i++) {
        nsAnimate._observer.observe(targets[i]);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // 6. Auto-init on DOMContentLoaded
  // ---------------------------------------------------------------------------

  function autoInit() {
    nsAccordion.initAll();
    nsAnimate.setup();
    nsFavorites.count(); // sync count badges on page load
    nsCart.getCount();   // sync cart count badges on page load
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    // DOM already parsed (script deferred or placed at bottom of body)
    autoInit();
  }

  // ---------------------------------------------------------------------------
  // Expose globals
  // ---------------------------------------------------------------------------

  window.nsFavorites      = nsFavorites;
  window.nsRecentlyViewed = nsRecentlyViewed;
  window.nsCart           = nsCart;
  window.nsAccordion      = nsAccordion;
  window.nsAnimate        = nsAnimate;

})();
